import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PublicHeader from './PublicHeader';
import { supabase } from './supabaseClient';
import { clearCustomerSession, getCustomerSession } from './customerSession';
import './PublicHome.css';
import './CustomerAuth.css';

function parseRpcResult(data) {
  if (data == null) return null;
  if (typeof data === 'object' && !Array.isArray(data)) return data;
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  }
  return null;
}

function CustomerProfile() {
  const navigate = useNavigate();
  const skipSessionRedirectRef = React.useRef(false);
  const [session, setSession] = React.useState(() => getCustomerSession());
  const [deletePassword, setDeletePassword] = React.useState('');
  const [deleteError, setDeleteError] = React.useState('');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);
  const [confirmBusy, setConfirmBusy] = React.useState(false);
  const [successOpen, setSuccessOpen] = React.useState(false);

  React.useEffect(() => {
    const sync = () => {
      const s = getCustomerSession();
      setSession(s);
      if (!s && !skipSessionRedirectRef.current) {
        navigate('/sign-in', { replace: true });
      }
    };
    sync();
    window.addEventListener('odus-customer-session', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('odus-customer-session', sync);
      window.removeEventListener('storage', sync);
    };
  }, [navigate]);

  const dismissSuccessModal = React.useCallback(() => {
    skipSessionRedirectRef.current = false;
    setSuccessOpen(false);
    navigate('/', { replace: true });
  }, [navigate]);

  React.useEffect(() => {
    if (!confirmDeleteOpen && !successOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [confirmDeleteOpen, successOpen]);

  React.useEffect(() => {
    if (!confirmDeleteOpen && !successOpen) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (confirmBusy) return;
      if (confirmDeleteOpen) setConfirmDeleteOpen(false);
      else if (successOpen) dismissSuccessModal();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [confirmDeleteOpen, successOpen, confirmBusy, dismissSuccessModal]);

  const handleDeleteFormSubmit = (e) => {
    e.preventDefault();
    setDeleteError('');
    if (!session?.email) return;
    if (!deletePassword.trim()) {
      setDeleteError('Enter your current password to confirm.');
      return;
    }
    setConfirmDeleteOpen(true);
  };

  const closeConfirmModal = () => {
    if (confirmBusy) return;
    setConfirmDeleteOpen(false);
  };

  const handleConfirmDelete = async () => {
    if (!session?.email || !deletePassword.trim()) return;
    if (!supabase) {
      setConfirmDeleteOpen(false);
      setDeleteError('Supabase is not configured.');
      return;
    }

    setConfirmBusy(true);
    setDeleteError('');
    try {
      const { data, error: rpcError } = await supabase.rpc('delete_customer_account', {
        p_email: session.email,
        p_password: deletePassword,
      });

      if (rpcError) throw rpcError;

      const row = parseRpcResult(data);
      if (!row || !row.ok) {
        setConfirmDeleteOpen(false);
        setDeleteError(row?.error || 'Could not delete account.');
        return;
      }

      skipSessionRedirectRef.current = true;
      clearCustomerSession();
      setConfirmDeleteOpen(false);
      setDeletePassword('');
      setSuccessOpen(true);
    } catch (err) {
      setConfirmDeleteOpen(false);
      setDeleteError(
        err?.message ||
          'Could not delete account. Run the latest sql/add_customer_accounts_supabase.sql in Supabase.'
      );
    } finally {
      setConfirmBusy(false);
    }
  };

  if (!session && !successOpen) {
    return (
      <div className="ph-root ct-page">
        <PublicHeader />
        <main className="ca-page">
          <p className="ca-lead">Redirecting…</p>
        </main>
      </div>
    );
  }

  return (
    <div className="ph-root ct-page">
      <PublicHeader />

      <main className="ca-page">
        {session ? (
          <div className="ca-card">
            <h1 className="ca-title">Your profile</h1>
            <p className="ca-lead">Signed in as a customer.</p>

            <div className="cp-profile-block">
              <div className="cp-row">
                <span className="cp-label">Name</span>
                <span className="cp-value">{session.fullName || '—'}</span>
              </div>
              <div className="cp-row">
                <span className="cp-label">Email</span>
                <span className="cp-value">{session.email}</span>
              </div>
            </div>

            <section className="cp-danger" aria-labelledby="cp-delete-heading">
              <h2 id="cp-delete-heading" className="cp-danger-title">
                Delete account
              </h2>
              <p className="cp-danger-text">
                This permanently removes your customer account. You will not be able to log in again
                with this email unless you register a new account.
              </p>
              <form className="ca-form" onSubmit={handleDeleteFormSubmit}>
                {deleteError ? (
                  <p className="ca-error" role="alert">
                    {deleteError}
                  </p>
                ) : null}
                <div className="ca-field">
                  <label className="ca-label" htmlFor="cp-delete-password">
                    Confirm with your password
                  </label>
                  <input
                    id="cp-delete-password"
                    className="ca-input"
                    type="password"
                    autoComplete="current-password"
                    value={deletePassword}
                    onChange={(e) => setDeletePassword(e.target.value)}
                    placeholder="Current password"
                  />
                </div>
                <button type="submit" className="ph-btn ca-delete-btn">
                  Delete account
                </button>
              </form>
            </section>

            {confirmDeleteOpen ? (
              <div
                className="cp-modal-root"
                role="presentation"
                onClick={closeConfirmModal}
              >
                <div
                  className="cp-modal cp-modal--warn"
                  role="alertdialog"
                  aria-modal="true"
                  aria-labelledby="cp-modal-confirm-title"
                  aria-describedby="cp-modal-confirm-desc"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="cp-modal-icon cp-modal-icon--warn" aria-hidden="true">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M12 9v4m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4c-.77-1.33-2.69-1.33-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z"
                        stroke="currentColor"
                        strokeWidth="1.75"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <h2 id="cp-modal-confirm-title" className="cp-modal-title">
                    Delete your account?
                  </h2>
                  <p id="cp-modal-confirm-desc" className="cp-modal-text">
                    This cannot be undone. You will not be able to sign in again with{' '}
                    <strong>{session.email}</strong> unless you create a new account.
                  </p>
                  <div className="cp-modal-actions">
                    <button
                      type="button"
                      className="cp-modal-btn cp-modal-btn--ghost"
                      onClick={closeConfirmModal}
                      disabled={confirmBusy}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="cp-modal-btn cp-modal-btn--danger"
                      onClick={handleConfirmDelete}
                      disabled={confirmBusy}
                    >
                      {confirmBusy ? 'Deleting…' : 'Yes, delete forever'}
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            <p className="ca-footer" style={{ marginTop: 24 }}>
              <Link to="/" className="ca-footer-link">
                ← Back to home
              </Link>
            </p>
          </div>
        ) : null}
      </main>

      {successOpen ? (
        <div className="cp-modal-root" role="presentation">
          <div
            className="cp-modal cp-modal--success"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cp-modal-success-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cp-modal-icon cp-modal-icon--success" aria-hidden="true">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
                <path
                  d="M8 12l2.5 2.5L16 9"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <h2 id="cp-modal-success-title" className="cp-modal-title">
              Account removed
            </h2>
            <p className="cp-modal-text">
              Your profile has been permanently deleted. Thank you for using ODUS.
            </p>
            <div className="cp-modal-actions cp-modal-actions--single">
              <button
                type="button"
                className="cp-modal-btn cp-modal-btn--primary"
                onClick={dismissSuccessModal}
              >
                Back to home
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default CustomerProfile;
